import React from 'react';
import { colorWithAlpha } from '../../../common/color';

function buildOccurrenceTooltip(occurrence) {
  const tooltipStart = occurrence.starts_previous_day
    ? `Vortag ${occurrence.actual_start ?? occurrence.start}`
    : (occurrence.actual_start ?? occurrence.start);
  const tooltipEnd = occurrence.ends_next_day
    ? `Folgetag ${occurrence.actual_end ?? occurrence.end}`
    : (occurrence.actual_end ?? occurrence.end);

  return `${occurrence.context_label} | ${occurrence.anchor_label} | ${tooltipStart} - ${tooltipEnd}`;
}

export default function WorkflowOccurrenceTrack({
  occurrence,
  workflowId,
  workflowName,
  workflowRowKey,
  workflowEntity,
  workflowRowId,
  color,
  iconClassName,
  zoom,
  timeStringToMinutes,
  timeToPixels,
  isTaskHoverActive = false,
  isOccurrenceHoverActive = false,
  isSelected = false,
  isSelectionPreview = false,
  selectionModeActive = false,
  onHoverChange,
  onShiftDragStart,
  onSelectionToggle,
}) {
  const longPressTimerRef = React.useRef(null);
  const startMin = timeStringToMinutes(occurrence.start);
  const endMin = timeStringToMinutes(occurrence.end);
  const left = timeToPixels(startMin, zoom);
  const width = Math.max(1, timeToPixels(endMin, zoom) - left);
  const showLabel = isTaskHoverActive ? width >= 34 : width >= 84;
  const showAnchorLabel = !isTaskHoverActive && width >= 148;
  const workflowColor = color || '#ffffff';
  const trackTone = {
    '--workflow-color': workflowColor,
    '--workflow-border': colorWithAlpha(workflowColor, 0.28, 'rgba(143, 110, 46, 0.28)'),
    '--workflow-bg': colorWithAlpha(workflowColor, 0.16, 'rgba(255, 255, 255, 0.08)'),
    '--workflow-accent': colorWithAlpha(workflowColor, 0.72, 'rgba(143, 110, 46, 0.72)'),
    '--workflow-occurrence-track-bg': colorWithAlpha(workflowColor, 0.16, 'rgba(255, 255, 255, 0.08)'),
    '--workflow-occurrence-track-border': colorWithAlpha(workflowColor, 0.28, 'rgba(143, 110, 46, 0.28)'),
    '--workflow-occurrence-track-highlight': colorWithAlpha(workflowColor, 0.22, 'rgba(255, 255, 255, 0.11)'),
  };
  const trackPosition = {
    left: `${left}px`,
    width: `${width}px`,
  };
  const tooltipTitle = buildOccurrenceTooltip(occurrence);
  const startMarkerTime = occurrence.starts_previous_day
    ? `Vortag ${occurrence.actual_start ?? occurrence.start}`
    : (occurrence.actual_start ?? occurrence.start);
  const endMarkerTime = occurrence.ends_next_day
    ? `Folgetag ${occurrence.actual_end ?? occurrence.end}`
    : (occurrence.actual_end ?? occurrence.end);

  const nextHoverStateBase = {
    kind: 'occurrence',
    occurrenceId: occurrence.occurrence_id,
    workflowId,
    color: workflowColor,
    combinedLabel: `${occurrence.context_label} ${startMarkerTime} - ${endMarkerTime}`,
    markers: [
      {
        key: 'start',
        type: 'start',
        minutes: startMin,
        label: `Workflow Start ${startMarkerTime}`,
        timeText: startMarkerTime,
      },
      {
        key: 'end',
        type: 'end',
        minutes: endMin,
        label: `Workflow Ende ${endMarkerTime}`,
        timeText: endMarkerTime,
      },
    ],
  };

  function shouldForceWorkflowHoverLabel(currentTarget) {
    const scroller = currentTarget?.closest?.('.timeline-scroll');
    if (!scroller || typeof currentTarget?.getBoundingClientRect !== 'function') {
      return false;
    }

    const trackRect = currentTarget.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    return trackRect.left < (scrollerRect.left + 72);
  }

  function buildHoverState(event) {
    return {
      ...nextHoverStateBase,
      forceWorkflowHoverLabel: shouldForceWorkflowHoverLabel(event?.currentTarget),
    };
  }

  function clearLongPressTimer() {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }

  React.useEffect(() => () => {
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  function toggleSelection(event) {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    clearLongPressTimer();
    onSelectionToggle?.({
      occurrence,
      workflowId,
      workflowName,
      workflowEntity,
      workflowRowId,
      workflowRowKey,
      color: workflowColor,
    });
  }

  function handlePointerDown(event) {
    if (event.ctrlKey || event.metaKey) {
      toggleSelection(event);
      return;
    }

    if (event.pointerType === 'touch') {
      if (selectionModeActive) {
        toggleSelection(event);
        return;
      }

      clearLongPressTimer();
      longPressTimerRef.current = window.setTimeout(() => {
        onSelectionToggle?.({
          occurrence,
          workflowId,
          workflowName,
          workflowEntity,
          workflowRowId,
          workflowRowKey,
          color: workflowColor,
        });
      }, 480);
      return;
    }

    onShiftDragStart?.(event, occurrence);
  }

  return (
    <>
      <div
        className={[
          'workflow-occurrence-track',
          isSelected ? 'is-selected' : '',
          isSelectionPreview ? 'is-selection-preview' : '',
          occurrence.starts_previous_day ? 'is-clipped-start' : '',
          occurrence.ends_next_day ? 'is-clipped-end' : '',
        ].filter(Boolean).join(' ')}
        style={{
          ...trackPosition,
          ...trackTone,
        }}
        data-bs-toggle="tooltip"
        data-bs-title={tooltipTitle}
        data-bs-placement="top"
        data-timeline-selectable-workflow="true"
        data-testid="timeline-workflow-occurrence"
        data-workflow-row-key={workflowRowKey}
        data-workflow-entity={workflowEntity}
        data-workflow-row-id={workflowRowId ?? ''}
        data-workflow-name={workflowName}
        aria-hidden="true"
        onMouseEnter={(event) => onHoverChange?.(buildHoverState(event))}
        onMouseLeave={() => onHoverChange?.(null)}
        onPointerDown={handlePointerDown}
        onPointerMove={clearLongPressTimer}
        onPointerUp={clearLongPressTimer}
        onPointerCancel={clearLongPressTimer}
      />

      {showLabel ? (
        <div
          className="workflow-occurrence-track-label-shell"
          style={trackPosition}
          aria-hidden="true"
        >
          <span
            className={[
              'workflow-occurrence-track-label',
              isSelected ? 'is-selected' : '',
              isSelectionPreview ? 'is-selection-preview' : '',
              isTaskHoverActive ? 'is-icon-only' : '',
            ].filter(Boolean).join(' ')}
            onMouseEnter={(event) => onHoverChange?.(buildHoverState(event))}
            onMouseLeave={() => onHoverChange?.(null)}
            onPointerDown={handlePointerDown}
            onPointerMove={clearLongPressTimer}
            onPointerUp={clearLongPressTimer}
            onPointerCancel={clearLongPressTimer}
          >
            <i className={iconClassName} aria-hidden="true" />
            {!isTaskHoverActive ? (
              <>
                <span className="workflow-occurrence-track-context">
                  {isOccurrenceHoverActive ? workflowName : occurrence.context_label}
                </span>
                {showAnchorLabel ? (
                  <span className="workflow-occurrence-track-anchor">
                    {occurrence.anchor_label}
                  </span>
                ) : null}
              </>
            ) : null}
          </span>
        </div>
      ) : null}
    </>
  );
}
